/* eslint-disable react-refresh/only-export-components */
import { useMemo, useState } from 'react'
import { AppDialog, type AppDialogAction } from '../../Common/AppDialog'
import { Select, type SelectOption } from '../../Common/Select'
import { TextInput } from '../../Common/TextInput'
import { Textarea } from '../../Common/Textarea'
import { useNls } from '../../../nls/nlsProvider'

export type SupportAccessReasonCode =
  | 'SUPPORT_TICKET'
  | 'INCIDENT'
  | 'ABUSE_REVIEW'
  | 'USER_REQUEST'
  | 'LEGAL_REQUEST'
  | 'DATA_MIGRATION'
  | 'ROUTINE_REVIEW'

export {
  SupportAccessReasonProvider,
  useSupportAccessReason,
  type SupportAccessReasonContextValue,
  type EnsureAccessReasonOptions,
} from './SupportAccessReasonContext'

export interface SupportAccessReason {
  code: SupportAccessReasonCode
  ticketId: string
  note?: string
  linkedId?: string
}

export interface SupportAccessReasonDialogProps {
  open: boolean
  defaultCode?: SupportAccessReasonCode
  defaultTicketId?: string
  defaultNote?: string
  onSubmit: (reason: SupportAccessReason) => void
  onCancel: () => void
}

function SupportAccessReasonDialogBody({
  defaultCode = 'SUPPORT_TICKET',
  defaultTicketId = '',
  defaultNote = '',
  onSubmit,
  onCancel,
}: Omit<SupportAccessReasonDialogProps, 'open'>) {
  const { t } = useNls()
  const [code, setCode] = useState<SupportAccessReasonCode>(defaultCode)
  const [ticketId, setTicketId] = useState(defaultTicketId)
  const [note, setNote] = useState(defaultNote)

  const reasonOptions: SelectOption<SupportAccessReasonCode>[] = useMemo(
    () => [
      { value: 'SUPPORT_TICKET', label: t('NLSOwnerConsoleReasonSupportTicket') },
      { value: 'INCIDENT', label: t('NLSOwnerConsoleReasonIncident') },
      { value: 'ABUSE_REVIEW', label: t('NLSOwnerConsoleReasonAbuseReview') },
      { value: 'USER_REQUEST', label: t('NLSOwnerConsoleReasonUserRequest') },
      { value: 'LEGAL_REQUEST', label: t('NLSOwnerConsoleReasonLegalRequest') },
      { value: 'DATA_MIGRATION', label: t('NLSOwnerConsoleReasonDataMigration') },
      { value: 'ROUTINE_REVIEW', label: t('NLSOwnerConsoleReasonRoutineReview') },
    ],
    [t],
  )

  const canContinue = Boolean(code && ticketId.trim())

  const actions: AppDialogAction[] = [
    {
      label: t('NLSOwnerConsoleAccessReasonCancelAction'),
      variant: 'ghost',
      onClick: onCancel,
      testId: 'support-access-reason-cancel',
    },
    {
      label: t('NLSOwnerConsoleAccessReasonContinueAction'),
      variant: 'primary',
      disabled: !canContinue,
      onClick: () => {
        if (!canContinue) return
        onSubmit({
          code,
          ticketId: ticketId.trim(),
          ...(note.trim() ? { note: note.trim() } : {}),
        })
      },
      testId: 'support-access-reason-continue',
    },
  ]

  return (
    <AppDialog
      open={true}
      title={t('NLSOwnerConsoleAccessReasonDialogTitle')}
      onDismiss={onCancel}
      actions={actions}
      size="sm"
      data-testid="support-access-reason-dialog"
      message={
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingTop: '8px' }}>
          <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary, #94a3b8)' }}>
            {t('NLSOwnerConsoleAccessReasonDialogSubtitle')}
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label htmlFor="support-access-reason-code" style={{ fontSize: '13px', fontWeight: 500 }}>
              {t('NLSOwnerConsoleAccessReasonCodeLabel')}
            </label>
            <Select
              id="support-access-reason-code"
              value={code}
              options={reasonOptions}
              onChange={(val) => setCode(val)}
              ariaLabel={t('NLSOwnerConsoleAccessReasonCodeLabel')}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label htmlFor="support-access-reason-ticket-id" style={{ fontSize: '13px', fontWeight: 500 }}>
              {t('NLSOwnerConsoleAccessReasonTicketIdLabel')}
            </label>
            <TextInput
              id="support-access-reason-ticket-id"
              value={ticketId}
              onChange={setTicketId}
              placeholder={t('NLSOwnerConsoleAccessReasonTicketIdPlaceholder')}
              block
              required
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label htmlFor="support-access-reason-note" style={{ fontSize: '13px', fontWeight: 500 }}>
              {t('NLSOwnerConsoleAccessReasonNoteLabel')}
            </label>
            <Textarea
              id="support-access-reason-note"
              value={note}
              onChange={setNote}
              placeholder={t('NLSOwnerConsoleAccessReasonNotePlaceholder')}
              maxLength={500}
              block
            />
          </div>
        </div>
      }
    />
  )
}

export function SupportAccessReasonDialog({
  open,
  defaultCode = 'SUPPORT_TICKET',
  defaultTicketId = '',
  defaultNote = '',
  onSubmit,
  onCancel,
}: SupportAccessReasonDialogProps) {
  if (!open) return null

  return (
    <SupportAccessReasonDialogBody
      key={`${defaultCode}:${defaultTicketId}:${defaultNote}`}
      defaultCode={defaultCode}
      defaultTicketId={defaultTicketId}
      defaultNote={defaultNote}
      onSubmit={onSubmit}
      onCancel={onCancel}
    />
  )
}
